/* =====================================================================================
   Channel's partner and tender lists, from the database.

   Channel rendered a compiled seed constant. With a live backend that constant is empty
   by design, so a partner created through the form existed in channel.partners and
   nowhere on screen: the same defect Studio had before v_assets (0088), found again on
   22 October 2026 when the first real distributor vanished on creation. This file maps
   the channel tables into the shapes the module already renders; the seed path remains
   for demonstration tenants.
   ===================================================================================== */

import { explain } from './dberrors';
import type { Partner, Tender } from '../data/commercial.config';

async function sb() { const { supabase } = await import('./supabase'); return supabase(); }

/* The upper bound of a daterange as it arrives over REST: "[2026-01-01,2027-01-01)". */
function rangeUpper(r: string | null): string {
  const m = /,([0-9-]+)[)\]]/.exec(r ?? '');
  return m ? m[1] : '';
}

export async function fetchPartnersLive(): Promise<Partner[]> {
  const s = await sb();
  const [partners, accounts, dd, grants, products] = await Promise.all([
    s.schema('channel').from('partners').select('*').order('created_at'),
    s.schema('crm').from('accounts').select('id, name, country'),
    s.schema('channel').from('due_diligence').select('*').order('assessment_date'),
    s.schema('channel').from('territory_grants').select('*').eq('status', 'active'),
    s.schema('pipeline').from('products').select('id, name'),
  ]);
  for (const r of [partners, accounts, dd, grants, products]) {
    if (r.error) throw new Error(explain(r.error.message, 'read the partners'));
  }
  const acc = new Map((accounts.data ?? []).map((a: any) => [a.id, a]));
  const prod = new Map((products.data ?? []).map((p: any) => [p.id, p.name]));
  /* Ordered by assessment_date, so the last write per partner is the current assessment. */
  const latestDd = new Map<string, any>();
  for (const d of dd.data ?? []) latestDd.set(d.partner_id, d);
  const byPartner = new Map<string, any[]>();
  for (const g of grants.data ?? []) {
    byPartner.set(g.partner_id, [...(byPartner.get(g.partner_id) ?? []), g]);
  }
  const today = new Date().toISOString().slice(0, 10);
  return (partners.data ?? []).map((p: any): Partner => {
    const a = acc.get(p.account_id);
    const d = latestDd.get(p.id);
    const ddStatus = !d ? 'none'
      : d.approved && d.next_review_due && d.next_review_due < today ? 'expired'
      : d.approved ? 'approved'
      : 'in_progress';
    const gs = byPartner.get(p.id) ?? [];
    const commit = gs.find((g) => g.min_commitment_value != null);
    return {
      id: p.id,
      name: a?.name ?? p.legal_entity_name ?? 'Unknown company',
      country: a?.country ?? '',
      type: p.partner_type as Partner['type'],
      status: p.status as Partner['status'],
      territories: gs.map((g) => ({
        country: g.country,
        product: prod.get(g.product_id) ?? 'All products',
        exclusivity: g.exclusivity,
        until: rangeUpper(g.valid_period),
      })),
      commitmentValue: commit?.min_commitment_value != null ? Number(commit.min_commitment_value) : undefined,
      commitmentCurrency: commit?.commitment_currency ?? undefined,
      dueDiligence: {
        status: ddStatus,
        riskRating: d?.risk_rating ?? undefined,
        nextReview: d?.next_review_due ?? undefined,
        sanctionsScreened: Boolean(d?.sanctions_screened),
        pepScreened: Boolean(d?.pep_screened),
        abacSigned: Boolean(d?.abac_certification_signed),
      },
      note: p.notes ?? undefined,
    };
  });
}

export async function fetchTendersLive(): Promise<Tender[]> {
  const s = await sb();
  const { data, error } = await s.schema('channel').from('tenders')
    .select('*').order('submission_deadline');
  if (error) throw new Error(explain(error.message, 'read the tenders'));
  return (data ?? []).map((t: any): Tender => ({
    id: t.id,
    ref: t.reference ?? '',
    title: t.title,
    authority: t.authority_name ?? '',
    country: t.country ?? '',
    estimatedValue: Number(t.estimated_value ?? 0),
    currency: t.currency ?? 'GBP',
    published: t.published_on ?? undefined,
    clarificationDeadline: t.clarification_deadline ?? undefined,
    submissionDeadline: t.submission_deadline,
    contractStart: t.contract_start ?? undefined,
    contractMonths: t.contract_months ?? undefined,
    bidDecision: (t.bid_decision ?? 'undecided') as Tender['bidDecision'],
    /* Two live statuses the seed vocabulary never had; shown as their nearest neighbour. */
    status: (t.status === 'clarification' ? 'submitted'
           : t.status === 'abandoned' ? 'cancelled' : t.status) as Tender['status'],
    note: t.description ?? undefined,
  }));
}

/* One assessment, recorded whole. Approval here is what unblocks ordering, so approving
   also moves a prospective partner to active: the status the Add-partner dialog promises. */
export interface DdForm {
  sanctionsScreened: boolean; sanctionsResult: string;
  pepScreened: boolean; pepResult: string;
  adverseMediaChecked: boolean; adverseMediaResult: string;
  beneficialOwnershipVerified: boolean;
  governmentOfficialLinks: string;
  abacSigned: boolean; trainingCompleted: boolean;
  riskRating: string; conditions: string; nextReviewDue: string; notes: string;
  approved: boolean;
}

export async function recordDueDiligence(partnerId: string, f: DdForm) {
  const s = await sb();
  const { data: me } = await s.schema('app').rpc('current_member_id');
  const today = new Date().toISOString().slice(0, 10);
  const { error } = await s.schema('channel').from('due_diligence').insert({
    partner_id: partnerId,
    assessed_by: me ?? null,
    risk_rating: f.riskRating,
    sanctions_screened: f.sanctionsScreened,
    sanctions_result: f.sanctionsResult || null,
    pep_screened: f.pepScreened,
    pep_result: f.pepResult || null,
    adverse_media_checked: f.adverseMediaChecked,
    adverse_media_result: f.adverseMediaResult || null,
    beneficial_ownership_verified: f.beneficialOwnershipVerified,
    government_official_links: f.governmentOfficialLinks || null,
    abac_certification_signed: f.abacSigned,
    abac_certification_date: f.abacSigned ? today : null,
    training_completed: f.trainingCompleted,
    approved: f.approved,
    approved_by: f.approved ? (me ?? null) : null,
    approved_on: f.approved ? today : null,
    conditions: f.conditions || null,
    next_review_due: f.nextReviewDue,
    notes: f.notes || null,
  });
  if (error) throw new Error(explain(error.message, 'record the due diligence'));
  if (f.approved && f.riskRating !== 'unacceptable') {
    const { error: e2 } = await s.schema('channel').from('partners')
      .update({ status: 'active', onboarded_on: today })
      .eq('id', partnerId).in('status', ['prospective', 'due_diligence']);
    if (e2) throw new Error(explain(e2.message, 'activate the partner'));
  }
}
